import {
	Attempt, ElibraryScoreDataSource, Question, Quiz
} from "@sabyr-dev/mist_elibrary/datasource";
import { Serializer } from "@sabyr-dev/mist_core/model/datasource";
import { AcademyAttemptState } from "./state";
import {
	AttemptScore, ReviewScore
} from "@sabyr-dev/mist_core/model/datasource/score";
import { ListItem } from "@sabyr-dev/mist_core/model/datasource/list";
import { AcademyLocalDatabase } from "./idb";
import {
	getConfig,
	getCookie,
	getProductId,
	getServerAttempts
} from "./academy";

export interface AcademyAttemptTableRecord {
	id: Attempt;
	complete: boolean;
	date: Date;
	test: string
	time: number;
	timeFormatted: string;
	correct: number;
	incorrect: number;
	unanswered: number;
	total: number;
	percent: number;
	first?: string;
}

export class AcademyScoreDataSource extends ElibraryScoreDataSource {
	local: AcademyLocalDatabase;

	constructor(item: Serializer<Quiz>) {
		super(item);
		this.local = new AcademyLocalDatabase();
	}

	async restoreAttempts(): Promise<void> {
		const db = await this.local.getDb();
		const attempts = getServerAttempts();
		const product = getProductId();
		const cookie = getCookie();
		const keys: IDBValidKey[] = await db.getAllKeys(
			AcademyLocalDatabase.attempt,
			IDBKeyRange.bound(`${product}.0000000000`, `${product}.9999999999`)
		);
		await this.local.write(AcademyLocalDatabase.attempt, `restore${Date.now()}`, async (transaction) => {
			await Promise.all(
				attempts.map(attempt => {
					const key = this.local.getAttemptKey(product, attempt.id, cookie);
					if (keys.findIndex(k => k == key) >= 0) {
						return Promise.resolve();
					}
					return transaction.store.put({
						version: 2, timestamp: new Date().toISOString(),
						times: {}, filters: [],
						cookie: cookie, productId: product,
						testId: `${attempt.set_id.join(',')}-${attempt.test_id}`,
						attemptId: attempt.id, testName: attempt.name,
						mode: attempt.complete ? 'result' : 'test', started: new Date(attempt.started),
						userData: Object.entries(attempt.answers ?? {}).reduce((result, [key, answer]) => ({
							...result,
							[key]: {
								answer: answer.answer,
								status: answer.correct ? 'correct': 'incorrect'
							}
						}), {}),
					} as AcademyAttemptState, key);
				})
			);
			return true;
		});
	}

	async getAttempts(): Promise<AcademyAttemptTableRecord[]> {
		const db = await this.local.getDb();
		const product = getProductId();
		const records: AcademyAttemptState[] = await db.getAll(
			AcademyLocalDatabase.attempt,
			IDBKeyRange.bound(`${product}.0000000000`, `${product}.9999999999`)
		);
		const cookie = getCookie();
		return records.filter(r => r.cookie == cookie).map(record => {
			const time = Object.values(record.times).reduce((result, time) => result + time, 0);
			const complete = record.mode == 'result';
			const stats = {
				correct: complete ? 0 : undefined,
				incorrect: complete ? 0 : undefined,
				unanswered: 0,
				total: 0,
				percent: 0,
				first: undefined
			};
			for (const [key, item] of Object.entries(record.userData)) {
				stats.total++;
				stats.unanswered += !item.status || item.status == 'unanswered' ? 1 : 0;
				if (!stats.first && (!item.status || item.status == 'unanswered')) {
					stats.first = key;
				}
				if (complete) {
					stats.correct += item.status == 'correct' ? 1 : 0;
					stats.incorrect += item.status == 'incorrect' ? 1 : 0;
					stats.percent = stats.correct / stats.total;
				}
			}
			return {
				id: record.attemptId,
				date: record.started,
				test: record.testName,
				complete,
				time,
				timeFormatted: new Date(time).toLocaleTimeString(navigator.language ?? navigator.languages[0], time > 60 * 60 * 1000 ?
					{
						hour: 'numeric',
						minute: '2-digit',
						second: '2-digit',
						timeZone: 'utc',
						hour12: false
					} :
					{ minute: 'numeric', second: '2-digit', timeZone: 'utc', hour12: false }
				),
				...stats
			};
		});
	}

	async getOverviewScores(): Promise<AttemptScore<Attempt, Quiz>> {
		const db = await this.local.getDb();
		const product = getProductId();
		const records: AcademyAttemptState[] = await db.getAll(
			AcademyLocalDatabase.attempt,
			IDBKeyRange.bound(`${product}.0000000000`, `${product}.9999999999`)
		);
		const scores = {};
		for (const record of records) {
			if (record.mode != 'result') {
				continue;
			}
			const answered_count = Object.values(record.userData)
				.filter(item => item.status != 'unanswered').length;
			const correct_count = Object.values(record.userData)
				.filter(item => item.status == 'correct').length;
			const total_count = Object.values(record.userData).length;
			if (
				!(record.testId in scores)
				|| (correct_count / (total_count || 1)) > (
					scores[record.testId].correct_count / (scores[record.testId].total_count || 1)
				)
			) {
				scores[record.testId] = {
					id: record.attemptId, answered_count, correct_count, total_count
				};
			}
		}
		return scores;
	}

	async getTableScores(): Promise<ReviewScore[]> {
		const db = await this.local.getDb();
		const state = await db.get(AcademyLocalDatabase.attempt, this.local.getAttemptKey()) as AcademyAttemptState;
		const items = Array.from(document.querySelectorAll<HTMLElement>('.item-list .item'))
			.map(item => item.dataset as ListItem<Question>);
		const config = getConfig();
		const unlocked = !config.hasEmail ? items.slice(0, config.freeQuestions) : items;
		const sections: Record<string, ReviewScore> = unlocked.reduce((result, item) => ({
			...result,
			[item.section_id]: {
				section: config.hasEmail ? item.section : 'Unlocked',
				time: (result[item.section_id]?.time ?? 0) + (state.times[item.id] ?? 0),
				correct: (result[item.section_id]?.correct ?? 0) + (state.userData[item.id]?.status == 'correct' ? 1 : 0),
				incorrect: (result[item.section_id]?.incorrect ?? 0) + (state.userData[item.id]?.status == 'incorrect' ? 1 : 0),
				unanswered: (result[item.section_id]?.unanswered ?? 0) + (!['correct', 'incorrect'].includes(state.userData[item.id]?.status) ? 1 : 0),
				total: (result[item.section_id]?.total ?? 0) + 1,
				percent: ((result[item.section_id]?.correct ?? 0) + (state.userData[item.id]?.status == 'correct' ? 1 : 0))
					/ ((result[item.section_id]?.total ?? 0) + 1),
			}
		}), {});
		if (!config.hasEmail) {
			sections['locked'] = {
				section: 'Locked',
				time: '0',
				correct: 0,
				incorrect: 0,
				unanswered: items.length - config.freeQuestions,
				total: items.length - config.freeQuestions,
				percent: 0
			};
		}
		return Object.values(sections).map(section => ({
			...section,
			number: section.time,
			time: new Date(section.time).toLocaleTimeString(undefined, (parseInt(section.time) > 60 * 60 * 1000 ? {
				hour: 'numeric',
				minute: '2-digit',
				second: '2-digit',
				timeZone: 'utc',
				hour12: false
			} : {
				minute: 'numeric', second: '2-digit', timeZone: 'utc', hour12: false
			}))
		}));
	}

	async getTotalScores(): Promise<ReviewScore> {
		const db = await this.local.getDb();
		const state = await db.get(AcademyLocalDatabase.attempt, this.local.getAttemptKey()) as AcademyAttemptState;
		const config = getConfig();
		const items = Array.from(document.querySelectorAll<HTMLElement>('.item-list .item'))
			.map(item => item.dataset as ListItem<Question>)
			.filter((item, index) => config.hasEmail || index < config.freeQuestions);
		const total = items.reduce((result, item) => ({
			section: 'total',
			time: (result?.time ?? 0) + (state.times[item.id] ?? 0),
			correct: (result?.correct ?? 0) + (state.userData[item.id]?.status == 'correct' ? 1 : 0),
			incorrect: (result?.incorrect ?? 0) + (state.userData[item.id]?.status == 'incorrect' ? 1 : 0),
			unanswered: (result?.unanswered ?? 0) + (!['correct', 'incorrect'].includes(state.userData[item.id]?.status) ? 1 : 0),
			total: (result?.total ?? 0) + 1,
			percent: ((result?.correct ?? 0) + (state.userData[item.id]?.status == 'correct' ? 1 : 0))
				/ ((result?.total ?? 0) + 1),
		}), {} as ReviewScore & { time: number });
		return {
			...total,
			time: new Date(total.time).toLocaleTimeString(
				undefined,
				(total.time > 60 * 60 * 1000 ? {
					hour: 'numeric',
					minute: '2-digit',
					second: '2-digit',
					timeZone: 'utc',
					hour12: false
				} : {
					minute: 'numeric',
					second: '2-digit',
					timeZone: 'utc',
					hour12: false
				})
			)
		};
	}
}
